/* ============================================================================
 * ATELIER EDGE-TINT - product-card letterbox bands painted as an extension of
 * the photo's own edge pixels, so non-square images read as the photo
 * continuing instead of blank white. Orientation-aware: a landscape photo's
 * bands continue its top/bottom ROWS; a portrait photo's bands continue its
 * left/right COLUMNS (keeping the wall/floor line at the photo's own height).
 *
 * LAYERING (Chris-approved, 2026-08-18): the et-bg underlay <img> (a 64px
 * rendition stretched + CSS-blurred, emitted in the card MARKUP so it arrives
 * with the page - never a white band) shows first; these accurate strips
 * OVERTAKE it - same-frame from cache, a brief crossfade only on the first
 * ever sight of an image.
 *
 * PERF CONTRACT (the reason this file looks the way it does):
 *  - NEVER touches the card's main <img> - no crossorigin, no attribute
 *    writes, no fetch-mode change. LCP images and preloads are unaffected.
 *  - Samples the SAME width=64 rendition the et-bg underlay displays (shared
 *    URL + CORS mode = one ~3KB download serves both), low priority + async
 *    decode, scheduled at idle. One sample per unique image per SESSION -
 *    strips persist in localStorage, so repeat views paint synchronously at
 *    script-run (same frame as a cached image, zero network, zero sampling).
 *  - Paints via a CSS variable + .is-tinted; adaptive entrance (.et-0 =
 *    instant when the tint lands before the photo is on screen). Scroll-time
 *    cost is zero - bands are plain backgrounds, no filters, no live layers.
 *
 * CONSUMERS: any .pc-im (shared product card - collections, home rails,
 * /search, range rails, wishlist grid) and .at-spc-im (header palette).
 * ::before rules live in atelier-collection.css (pc) and atelier-header.css
 * (palette). Collections re-tint after AJAX facet renders via the
 * #ProductGridContainer observer; JS-rendered surfaces also call
 * ATEdgeTint.scan(root) after building cards (safe to call any time).
 * ============================================================================ */
(function () {
  'use strict';
  var cache = {};       /* probeUrl -> {ar,t,b,l,r} strips, or false = failed */
  var pending = {};     /* probeUrl -> [boxes awaiting the in-flight probe] */
  var SEL = '.pc-im, .at-spc-im';

  /* strips persist in localStorage (~1KB/image) so REPEAT views paint the tint
     synchronously at script-run - the same frame as a cached image, no probe.
     Failures (false) are session-only. Storage errors are silently ignored. */
  var LS_KEY = 'at-edge-tint-v3', LS_CAP = 150, saveT = null;   /* v3: 64px probe, native-res sampling */
  try { cache = JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch (e) { cache = {}; }
  function persist() {
    clearTimeout(saveT);
    saveT = setTimeout(function () {
      try {
        var keep = {}, keys = Object.keys(cache), n = 0;
        for (var i = 0; i < keys.length && n < LS_CAP; i++) {
          if (cache[keys[i]]) { keep[keys[i]] = cache[keys[i]]; n++; }
        }
        localStorage.setItem(LS_KEY, JSON.stringify(keep));
      } catch (e) { /* quota/privacy mode: memory cache still works */ }
    }, 400);
  }

  /* the tiny sampling rendition - the SAME URL the et-bg underlay displays.
     TWO URL generations exist: Algolia records carry legacy cdn.shopify.com
     URLs (palette + /search cards); Liquid's image_url emits store-routed
     /cdn/shop/ paths (collections + all snippet cards) - same-origin, so the
     CORS canvas read is trivially safe there. Rejecting the second form was
     the "/collections never gets strips" bug. */
  function probeUrl(src) {
    if (!src) return null;
    try {
      var u = new URL(src, location.href);
      if (u.hostname.indexOf('cdn.shopify.com') === -1 &&
          u.pathname.indexOf('/cdn/shop/') === -1) return null;
      u.searchParams.set('width', '64');
      u.searchParams.delete('height');
      return u.href;
    } catch (e) { return null; }
  }

  function sample(im) {
    /* NATIVE-resolution sampling (no extra squash): the edge slice is 1/64 of
       the photo - a wide product cannot tint the band (the "wood sneaking
       into the wall" bug a 16x16 downscale caused). */
    var W = Math.max(2, Math.min(96, im.naturalWidth || 64));
    var H = Math.max(2, Math.min(96, im.naturalHeight || 64));
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    c.getContext('2d').drawImage(im, 0, 0, W, H);
    function strip(sx, sy, sw, sh) {
      var s = document.createElement('canvas'); s.width = sw; s.height = sh;
      s.getContext('2d').drawImage(c, sx, sy, sw, sh, 0, 0, sw, sh);
      return 'url(' + s.toDataURL() + ')';
    }
    return {
      ar: im.naturalWidth / (im.naturalHeight || 1),
      t: strip(0, 0, W, 1), b: strip(0, H - 1, W, 1),
      l: strip(0, 0, 1, H), r: strip(W - 1, 0, 1, H)
    };
  }

  /* constant strips, halves meeting behind the photo - no fade inside a band */
  function bg(d, tileAR) {
    return d.ar >= tileAR
      ? d.t + ' top / 100% 51% no-repeat, ' + d.b + ' bottom / 100% 51% no-repeat'
      : d.l + ' left / 51% 100% no-repeat, ' + d.r + ' right / 51% 100% no-repeat';
  }

  /* instant (no transition) when the tint lands BEFORE the photo is on screen
     (pre-first-paint sync scan, or the image hasn't finished loading yet);
     the short fade only softens a genuinely LATE tint over a visible photo. */
  var retryT = null;
  function queueRetry() {
    clearTimeout(retryT);
    retryT = setTimeout(function () { scan(document); }, 180);
  }
  function paint(box, d, sync) {
    var r = box.getBoundingClientRect();
    if (!r.width || !r.height) {
      /* box not laid out yet (early sync scan vs a server-rendered grid still
         behind its skeleton): UNMARK + retry. Without this the card stayed
         marked-but-unpainted forever - the recurring "/collections looks
         different from /search" bug (JS-built cards never hit this path). */
      var tries = +(box.dataset.etTries || 0) + 1;
      if (tries < 6) { box.dataset.etTries = tries; delete box.dataset.et; queueRetry(); }
      return;
    }
    /* instant (et-0) ONLY on the pre-first-paint sync pass - the box is not on
       screen yet so instant is truly invisible. Every ASYNC arrival fades: the
       card's white bands are already visible while the photo loads, so "tint
       beat the photo" still means "tint lands on a visible card" - painting it
       instantly there was the cold-load white->snap Chris kept seeing. */
    if (sync) box.classList.add('et-0');
    box.style.setProperty('--edge-tint', bg(d, r.width / r.height));
    box.classList.add('is-tinted');
  }

  function load(purl, box, sync) {
    if (cache[purl] === false) return;
    if (cache[purl]) { paint(box, cache[purl], sync); return; }
    if (pending[purl]) { pending[purl].push(box); return; }
    pending[purl] = [box];
    var im = new Image();
    im.crossOrigin = 'anonymous';
    try { im.fetchPriority = 'low'; } catch (e) { /* older browsers */ }
    im.decoding = 'async';
    im.onload = function () {
      var boxes = pending[purl] || []; delete pending[purl];
      try { cache[purl] = sample(im); } catch (e) { cache[purl] = false; return; }
      for (var i = 0; i < boxes.length; i++) paint(boxes[i], cache[purl], false);
      persist();
    };
    im.onerror = function () { cache[purl] = false; delete pending[purl]; };
    im.src = purl;
  }

  function scan(root, sync) {
    var boxes = (root && root.querySelectorAll ? root : document).querySelectorAll(SEL);
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      /* et-bg underlay entrance - ADAPTIVE: already loaded (cache) = instant,
         arriving late (cold first visit) = soft fade, never a harsh pop */
      var bgim = box.querySelector('.et-bg');
      if (bgim && !bgim.dataset.in) {
        bgim.dataset.in = '1';
        if (bgim.complete) bgim.classList.add('et-now', 'et-in');
        else bgim.addEventListener('load', function () { this.classList.add('et-in'); });
      }
      if (box.dataset.et) continue;
      var img = box.querySelector('img:not(.et-bg)');
      var purl = probeUrl(img && (img.currentSrc || img.src));
      if (!purl) continue;              /* placeholder / non-CDN: leave for a later scan */
      box.dataset.et = '1';
      load(purl, box, !!sync);
    }
  }

  /* idle with a timeout floor - on a busy loading page rIC can starve; the
     timeout keeps tints near the images without ever preempting layout work */
  var idle = window.requestIdleCallback
    ? function (f) { window.requestIdleCallback(f, { timeout: 1500 }); }
    : function (f) { setTimeout(f, 200); };
  function scanIdle(root) { idle(function () { scan(root); }); }

  /* collections: AJAX facet renders swap #ProductGridContainer - re-tint after */
  function watchGrid() {
    var grid = document.getElementById('ProductGridContainer');
    if (!grid || !window.MutationObserver) return;
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t); t = setTimeout(function () { scanIdle(grid); }, 120);
    }).observe(grid, { childList: true, subtree: true });
  }

  /* SYNCHRONOUS first scan at script-run (deferred = DOM parsed): cached
     strips paint pre-first-render; uncached ones start their probes now.
     The window-load re-scan is the safety net for anything the early pass
     could not lay hands on (paint() also self-retries zero-size boxes). */
  scan(document, true);
  watchGrid();
  window.addEventListener('load', function () { scanIdle(document); });

  /* probe exposed so card builders can reuse the SAME width=64 URL as the
     et-bg underlay (shared cache entry - one download serves display+sampling) */
  window.ATEdgeTint = { scan: scanIdle, probe: probeUrl };
})();
